import React, { useEffect, useState, createContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../store';
import { useGetAchOutLimits } from '../../../hooks/query/useGetAchOutLimits';
import ContentWrapper from '../../../generic-components/ContentWrapper';
import MainContent from '../../../generic-components/Layout/MainContent';
import AppLayout from '../../../generic-components/Layout';
import AchOutStepSwitch from '../components/AchOutStepSwitch';
import { useSendAchOutMutation } from '../../../hooks/mutations/useSendAchOutMutation';
import { VIEWS_MAP } from '../../../router/constants';
import {
  STEPS_MAP,
  INITIAL_ACH_OUT_RESPONSE,
  FLOW_INDEX_DATA,
  WIZARD_MODE_STEPS,
  SupplierPickerRoute,
  AddSupplierRoute,
} from '../constants';
import { validateFeatureFlag } from '../../../services/featureFlagService';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { handleOnAchOutError } from '../services/onAchOutErrorService';
import { IServerErrorResponse } from '../../../types';
import { FEATURE_FLAG } from '../../../constants/general';
import { CONTEXT_TYPES, DYNAMIC_NAVIGATION_TITLE_MAP, SUPPLIER_REFERRER_TYPES } from '../../Supplier/consts';
import { NavigationHeader } from '../../../components/NavigationHeader';
import { ISupplierModel } from '../../Supplier/types';
import { DYNAMIC_FORM_TYPES } from '../../../components/form/dynamicForm/types';
import { ContextTypes } from '../../../constants/pages';
import { useGetSupplierData } from '../../Supplier/hooks/useGetSupplierData';
import bugsnagHandler from '../../../services/bugsnag.service';
import { useUploadMoneyTransferConfirmation } from '../../../hooks/mutations/useUploadMoneyTransferConfirmation';
import { takeSnapshot } from '../../../services/snapshot.service';

export const AchOutContext = createContext<any>({});

const AchOutTransfer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const getBankTransferFormData = useStore((state) => state.getBankTransferFormData);
  const transferAmount = useStore((state) => state.transferAmount);
  const setAccountNumber = useStore((state) => state.setAccountNumber);
  const setBusinessName = useStore((state) => state.setBusinessName);
  const setRoutingNumber = useStore((state) => state.setRoutingNumber);
  const setTransferAmount = useStore((state) => state.setTransferAmount);
  const setSupplierId = useStore((state) => state.setSupplierId);
  const setBillId = useStore((state) => state.setBillId);
  const resetBankTransferSlice = useStore((state) => state.resetBankTransferSlice);
  const setContextType = useStore((state) => state.setContextType);
  const contextType = useStore((state) => state.contextType);
  const supplierId = useStore((state) => state.supplierId);
  const billId = useStore((state) => state.billId);
  const {
    user: { customerId },
    getPersonalAccount,
  } = useStore((state) => state);
  const { accountExternalId } = getPersonalAccount();
  const [activeStep, setActiveStep] = useState<number>(STEPS_MAP.ZERO_STATE);
  const [enrollErrorData, setEnrollErrorData] = useState<{ errCode: number; errMsg: string }>({
    errCode: 0,
    errMsg: '',
  });
  const [achOutResponse, setAchOutResponse] = useState<any>(INITIAL_ACH_OUT_RESPONSE);
  const [snapshotFormData, setSnapShotFormData] = useState<unknown>();
  const { state: routeState } = useLocation();
  const { refetch: getAchOutLimits, data: achLimitsResponse } = useGetAchOutLimits(
    {
      customerId,
      accountExternalId,
    },
    () => {},
    (errData: { errCode: number; errMsg: string }) => {
      setEnrollErrorData(errData);
    },
  );
  const { refetch: getSupplierList } = useGetSupplierData({ });

  const { mutate: uploadScreenshot } = useUploadMoneyTransferConfirmation(
    {
      fileData: snapshotFormData,
      customerId,
      transactionId: achOutResponse?.transactionId,
    },
    () => {
      setSnapShotFormData(null);
    },
    () => {
      bugsnagHandler.notify(`Failed to send an ACH Out snapshot for customerId: ${customerId}`);
      setSnapShotFormData(null);
    },
  );

  const takeScreenShot = async () => {
    const formData: unknown = await takeSnapshot();
    setSnapShotFormData(formData);
  };

  const { mutate: sendAchOut, isLoading: isLoadingAchOut } = useSendAchOutMutation({
    customerId,
    accountId: accountExternalId,
    formData: getBankTransferFormData(),
    contextType: contextType || ContextTypes.ACH_OUT,
    billId,
    supplierId,
    onSuccess: (data: any) => {
      setAchOutResponse(data);
    },
    onError: (data: IServerErrorResponse) => {
      handleOnAchOutError(data, navigate, supplierId, transferAmount);
      bugsnagHandler.notify(
        `Failed to send a bank transfer for customerId: ${customerId} with the error: ${data?.response?.data?.errors?.[0]?.message || ''}`,
      );
    },
  });

  const dynamicStyle = useMemo(() => {
    if (WIZARD_MODE_STEPS.includes(activeStep)) {
      return FLOW_INDEX_DATA.CSS.APP_LAYOUT_WIZARD;
    }
    return FLOW_INDEX_DATA.CSS.LAYOUT;
  }, [activeStep]);

  const mfaRequiredProps = useMemo(
    () => ({
      mfaActionData: achOutResponse.mfaActionData,
    }),
    [achOutResponse],
  );

  const navigateToSupplierPicker = () =>
    navigate(SupplierPickerRoute, {
      state: { referrer: SUPPLIER_REFERRER_TYPES.ACH_OUT, closeUrl: `/${VIEWS_MAP.DASHBOARD}` },
    });
  const navigateToAddSupplierPage = () => {
    navigate(AddSupplierRoute, {
      state: {
        pageTitle: t('ACH_OUT_NEW.HEADER_TITLE'),
        formHeader: t('ACH_OUT_TITLES.FIRST_TIMER'),
        formType: DYNAMIC_FORM_TYPES.ACH_OUT,
        onExitDestination: `/${VIEWS_MAP.DASHBOARD}`,
      },
    });
  };
  const resetBankTransfer = () => {
    resetBankTransferSlice();
    setAchOutResponse(INITIAL_ACH_OUT_RESPONSE);
  };
  const handleMakeAnotherTransferClick = () => {
    resetBankTransfer();
    navigateToSupplierPicker();
  };
  const contextValue = useMemo(
    () => ({
      isLoading: isLoadingAchOut,
      achOutResponse,
      mfaRequiredProps,
      setActiveStep,
      setAchOutResponse,
    }),
    [mfaRequiredProps, achOutResponse, isLoadingAchOut],
  );
  const submitStepActions = {
    [STEPS_MAP.ENROLLMENT]: () => navigateToAddSupplierPage(),
    [STEPS_MAP.TRANSFER_AMOUNT]: () => setActiveStep(STEPS_MAP.REVIEW_CONFIRM),
    [STEPS_MAP.REVIEW_CONFIRM]: () => sendAchOut(),
    [STEPS_MAP.MFA]: () => setActiveStep(STEPS_MAP.TRANSFER_DONE),
    [STEPS_MAP.TRANSFER_DONE]: () => handleMakeAnotherTransferClick(),
  };
  const handleStepSubmit = () => {
    if ([STEPS_MAP.ZERO_STATE, STEPS_MAP.ENROLL_ERROR].includes(activeStep)) return;
    submitStepActions?.[activeStep as keyof typeof submitStepActions]();
  };
  const handleEditMode = (type: string) => {
    if (type === 'AMOUNT') {
      setActiveStep(STEPS_MAP.TRANSFER_AMOUNT);
    } else if (type === 'DETAILS') {
      navigate(SupplierPickerRoute, {
        state: {
          referrer: SUPPLIER_REFERRER_TYPES.ACH_OUT,
          closeUrl: `/${VIEWS_MAP}`,
          transferAmount,
        },
      });
    }
  };
  const updatePayeeDetails = (businessName: string, accountNumber: string | null, routingNumber: string | null) => {
    if (businessName) setBusinessName(businessName);
    if (accountNumber) setAccountNumber(accountNumber);
    if (routingNumber) setRoutingNumber(routingNumber);
  };
  const hasMissingBankDetails = (payeeDetails: ISupplierModel) =>
    !payeeDetails.achAccountData?.accountNumber || !payeeDetails.achAccountData?.routingNumber;
  const navigateToCompleteMissingDetails = (payeeDetails: ISupplierModel) => {
    navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.MANAGE.BASE}/${payeeDetails?.supplierId}`, {
      replace: true,
      state: {
        pageTitle: t('ACH_OUT_NEW.HEADER_TITLE'),
        formHeader: t('ACH_OUT_NEW_MISSING_DETAILS.TITLE'),
        formType: DYNAMIC_FORM_TYPES.ACH_OUT_MANDATORY,
        onExitDestination: `/${VIEWS_MAP.DASHBOARD}`,
        customBackUrl: routeState?.recoverUrl,
        isUpdateOnlyMode: true,
        transferAmount: routeState?.transferAmount || transferAmount,
      },
    });
  };
  const updateStoreFromRoute = () => {
    const payeeDetails = routeState?.payeeDetails;
    if (routeState?.transferAmount) setTransferAmount(routeState.transferAmount);
    if (routeState?.contextType) setContextType(routeState.contextType);
    if (routeState?.billId) setBillId(routeState.billId);
    if (payeeDetails?.supplierId) setSupplierId(payeeDetails?.supplierId);
    updatePayeeDetails(
      payeeDetails?.name,
      payeeDetails?.achAccountData?.accountNumber,
      payeeDetails?.achAccountData?.routingNumber,
    );
  };
  const checkFeatureFlag = () => {
    if (
      validateFeatureFlag(FEATURE_FLAG.NEW_ACH_OUT, () => {
        navigate(`/${VIEWS_MAP.DASHBOARD}`);
      })
    ) {
      getAchOutLimits();
    }
  };
  const handleCloseBankTransferFlow = () => {
    resetBankTransfer();
    let exitUrl = VIEWS_MAP.DASHBOARD;
    if (contextType === CONTEXT_TYPES.BILL_PAY) exitUrl = VIEWS_MAP.BILLS.BASE;
    navigate(`/${exitUrl}`, { state: undefined });
  };
  const handleTakeScreenshot = async () => {
    await takeScreenShot();
  };
  const fetchAndHandleInitialData = async () => {
    try {
      const supplierList = await getSupplierList();
      const supplierListLength = supplierList?.data?.suppliers?.length;
      if (supplierListLength > 0) {
        navigateToSupplierPicker();
      } else if (supplierListLength === 0) {
        setActiveStep(STEPS_MAP.ENROLLMENT);
      }
    } catch (e: unknown) {
      bugsnagHandler.notify(`Failed to fetch supplier list on Bank Transfer page for customerId: ${customerId}`);
    }
  };

  useEffect(() => {
    if (enrollErrorData.errCode && enrollErrorData.errMsg) {
      setActiveStep(STEPS_MAP.ENROLL_ERROR);
    }
  }, [enrollErrorData]);
  useEffect(() => {
    if (achLimitsResponse) {
      if (routeState?.payeeDetails) {
        updateStoreFromRoute();
        if (hasMissingBankDetails(routeState?.payeeDetails)) {
          navigateToCompleteMissingDetails(routeState.payeeDetails);
          return;
        }
        if (transferAmount || routeState?.transferAmount) {
          setActiveStep(STEPS_MAP.REVIEW_CONFIRM);
        } else {
          setActiveStep(STEPS_MAP.TRANSFER_AMOUNT);
        }
        return;
      }
      fetchAndHandleInitialData();
    }
  }, [achLimitsResponse]);
  useEffect(() => {
    if (achOutResponse?.mfaActionData?.status === 'MFA_CHALLENGE') {
      handleTakeScreenshot();
      setTimeout(() => {
        setActiveStep(STEPS_MAP.MFA);
      }, 0);
    } else if (achOutResponse?.status === 'SUCCESS' && achOutResponse?.transactionId) {
      if (activeStep === STEPS_MAP.REVIEW_CONFIRM) handleTakeScreenshot();
      setTimeout(() => {
        setActiveStep(STEPS_MAP.TRANSFER_DONE);
      }, 0);
    }
  }, [achOutResponse]);
  useEffect(() => {
    if (snapshotFormData) {
      uploadScreenshot();
    }
  }, [snapshotFormData]);
  useEffect(() => {
    if (activeStep === STEPS_MAP.TRANSFER_DONE && achOutResponse?.transactionId) {
      handleTakeScreenshot();
    }
  }, [activeStep]);
  useEffect(() => {
    trackMixPanel('View ACH to 3rd Party - Intro Screen');
    checkFeatureFlag();
    if (!routeState) resetBankTransferSlice();
    return () => {
      setTransferAmount('');
    };
  }, []);
  return (
    <AppLayout layoutStyle={dynamicStyle} navBarStyle='hidden' chatBotStyle='hidden' isWizardMode>
      {WIZARD_MODE_STEPS.includes(activeStep) && (
        <NavigationHeader
          onCloseAction={handleCloseBankTransferFlow}
          pageTitle={DYNAMIC_NAVIGATION_TITLE_MAP[CONTEXT_TYPES.ACH_OUT]}
        />
      )}
      <ContentWrapper styles='w-full h-full flex place-content-start relative top-0'>
        <AchOutContext.Provider value={contextValue}>
          <MainContent
            className={
              activeStep === STEPS_MAP.MFA
                ? FLOW_INDEX_DATA.CSS.MAIN_CONTENT_MFA
                : `${FLOW_INDEX_DATA.CSS.MAIN_CONTENT_WIDTH}`
            }
          >
            <AchOutStepSwitch
              handleEditMode={handleEditMode}
              handleStepSubmit={handleStepSubmit}
              enrollErrorData={enrollErrorData}
              activeStep={activeStep}
            />
          </MainContent>
        </AchOutContext.Provider>
      </ContentWrapper>
    </AppLayout>
  );
};

export default AchOutTransfer;
